import React, { useState } from 'react';
import { useUserProfile } from '../../contexts/UserProfileContext';
import Button from '../common/Button';
import Input from '../common/Input';
import Card from '../common/Card';
import { XMarkIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';

interface UserProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const UserProfileModal: React.FC<UserProfileModalProps> = ({ isOpen, onClose }) => {
  const { profile, updateProfile, addSkill, removeSkill, addInterest, removeInterest, addGoal, removeGoal } = useUserProfile();
  const [newSkill, setNewSkill] = useState('');
  const [newInterest, setNewInterest] = useState('');
  const [newGoal, setNewGoal] = useState('');

  if (!isOpen) return null;

  const handleAddSkill = () => {
    if (newSkill.trim()) {
      addSkill(newSkill);
      setNewSkill('');
    }
  };

  const handleAddInterest = () => {
    if (newInterest.trim()) {
      addInterest(newInterest);
      setNewInterest('');
    }
  };

  const handleAddGoal = () => {
    if (newGoal.trim()) {
      addGoal(newGoal);
      setNewGoal('');
    }
  };

  const businessStages = [
    { value: 'idea', label: '아이디어 단계' },
    { value: 'startup', label: '초기 창업' },
    { value: 'growth', label: '성장 단계' },
    { value: 'expansion', label: '확장 단계' },
    { value: 'mature', label: '성숙 단계' },
  ];

  const industries = [
    'IT/소프트웨어', '제조업', '서비스업', '유통/판매', '교육', '의료/헬스케어',
    '금융', '부동산', '건설', '식품/음료', '패션/뷰티', '엔터테인먼트',
    '운송/물류', '농업', '기타'
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">내 프로필 설정</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          <div className="space-y-6">
            {/* 기본 정보 */}
            <Card title="기본 정보">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="이름"
                  value={profile.name}
                  onChange={(value) => updateProfile({ name: value })}
                  placeholder="홍길동"
                />
                <Input
                  label="이메일"
                  type="email"
                  value={profile.email}
                  onChange={(value) => updateProfile({ email: value })}
                  placeholder="hong@example.com"
                />
                <Input
                  label="회사명"
                  value={profile.company}
                  onChange={(value) => updateProfile({ company: value })}
                  placeholder="(주)혁신기업"
                />
                <Input
                  label="직책"
                  value={profile.position}
                  onChange={(value) => updateProfile({ position: value })}
                  placeholder="대표이사"
                />
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    업종
                  </label>
                  <select
                    value={profile.industry}
                    onChange={(e) => updateProfile({ industry: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">업종 선택</option>
                    {industries.map(industry => (
                      <option key={industry} value={industry}>
                        {industry}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    사업 단계
                  </label>
                  <select
                    value={profile.businessStage}
                    onChange={(e) => updateProfile({ businessStage: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">사업 단계 선택</option>
                    {businessStages.map(stage => (
                      <option key={stage.value} value={stage.value}>
                        {stage.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </Card>

            {/* 비즈니스 정보 */}
            <Card title="비즈니스 정보">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="사업 유형"
                  value={profile.businessType}
                  onChange={(value) => updateProfile({ businessType: value })}
                  placeholder="온라인 플랫폼"
                />
                <Input
                  label="타겟 시장"
                  value={profile.targetMarket}
                  onChange={(value) => updateProfile({ targetMarket: value })}
                  placeholder="20-30대 직장인"
                />
                <Input
                  label="주요 제품/서비스"
                  value={profile.mainProduct}
                  onChange={(value) => updateProfile({ mainProduct: value })}
                  placeholder="교육 콘텐츠 플랫폼"
                />
                <Input
                  label="예산 규모"
                  value={profile.budget}
                  onChange={(value) => updateProfile({ budget: value })}
                  placeholder="1,000만원 이하"
                />
                <Input
                  label="사업 일정"
                  value={profile.timeline}
                  onChange={(value) => updateProfile({ timeline: value })}
                  placeholder="6개월 이내"
                />
              </div>
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  경험/배경
                </label>
                <textarea
                  value={profile.experience}
                  onChange={(e) => updateProfile({ experience: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="관련 경험이나 배경을 간략히 설명해주세요..."
                />
              </div>
            </Card>

            {/* 스킬 */}
            <Card title="보유 스킬">
              <div className="space-y-3">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newSkill}
                    onChange={(e) => setNewSkill(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddSkill()}
                    placeholder="스킬 추가 (예: 마케팅, 개발, 디자인)"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <Button onClick={handleAddSkill} size="sm">
                    <PlusIcon className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {profile.skills.map((skill, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 text-sm rounded-full"
                    >
                      {skill}
                      <button
                        onClick={() => removeSkill(skill)}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        <TrashIcon className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </Card>

            {/* 관심사 */}
            <Card title="관심 분야">
              <div className="space-y-3">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newInterest}
                    onChange={(e) => setNewInterest(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddInterest()}
                    placeholder="관심 분야 추가"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <Button onClick={handleAddInterest} size="sm">
                    <PlusIcon className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {profile.interests.map((interest, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-1 px-3 py-1 bg-green-100 text-green-800 text-sm rounded-full"
                    >
                      {interest}
                      <button
                        onClick={() => removeInterest(interest)}
                        className="text-green-600 hover:text-green-800"
                      >
                        <TrashIcon className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </Card>

            {/* 목표 */}
            <Card title="사업 목표">
              <div className="space-y-3">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newGoal}
                    onChange={(e) => setNewGoal(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddGoal()}
                    placeholder="목표 추가"
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <Button onClick={handleAddGoal} size="sm">
                    <PlusIcon className="w-4 h-4" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {profile.goals.map((goal, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-1 px-3 py-1 bg-purple-100 text-purple-800 text-sm rounded-full"
                    >
                      {goal}
                      <button
                        onClick={() => removeGoal(goal)}
                        className="text-purple-600 hover:text-purple-800"
                      >
                        <TrashIcon className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </Card>

            {/* 설정 */}
            <Card title="개인화 설정">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    선호하는 톤
                  </label>
                  <select
                    value={profile.preferredTone}
                    onChange={(e) => updateProfile({ preferredTone: e.target.value as 'formal' | 'casual' | 'professional' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="professional">전문적</option>
                    <option value="formal">공식적</option>
                    <option value="casual">친근한</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    언어
                  </label>
                  <select
                    value={profile.language}
                    onChange={(e) => updateProfile({ language: e.target.value as 'ko' | 'en' })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="ko">한국어</option>
                    <option value="en">English</option>
                  </select>
                </div>
              </div>
            </Card>
          </div>

          <div className="mt-8 flex justify-end gap-3">
            <Button variant="outline" onClick={onClose}>
              취소
            </Button>
            <Button onClick={onClose}>
              저장
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfileModal;